import React, { useState, useEffect } from 'react';
import { Search, Lock, Unlock, AlertTriangle, User } from 'lucide-react';
import axios from 'axios';

const BlockStudent = () => {
  const [blockedStudents, setBlockedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBlockedStudents();
  }, []);

  const fetchBlockedStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/students/blocked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlockedStudents(response.data);
    } catch (error) {
      console.error('Error fetching blocked students:', error);
      alert('Failed to fetch blocked students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (studentId, studentName) => {
    if (window.confirm(`Are you sure you want to unblock ${studentName}?`)) {
      try {
        await axios.post(
          `${API_URL}/api/students/${studentId}/unblock`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Student unblocked successfully!');
        fetchBlockedStudents();
      } catch (error) {
        console.error('Error unblocking student:', error);
        alert('Failed to unblock student. Please try again.');
      }
    }
  };

  const filteredStudents = blockedStudents.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blocked Students</h1>
          <p className="text-sm text-gray-500 mt-1">Manage blocked student accounts</p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              Blocked students cannot access the system or attend classes. They remain in the system for record-keeping purposes.
              Use this feature carefully and consider the impact on the student's academic progress.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Blocked Students</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{blockedStudents.length}</p>
          </div>
          <Lock className="h-16 w-16 text-red-500" />
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search blocked students by name, admission number, or email..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Details</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admission Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    {searchTerm ? 'No matching students found' : 'No blocked students'}
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                          <div className="text-sm text-gray-500">{student.email}</div>
                          <div className="text-sm text-gray-500">{student.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.admission_number}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">Course Info</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{student.admission_date}</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                        <Lock className="h-3 w-3" />
                        Blocked
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleUnblock(student.id, student.full_name)}
                        className="flex items-center gap-1 text-green-600 hover:text-green-900 font-medium text-sm ml-auto"
                      >
                        <Unlock className="h-4 w-4" />
                        Unblock
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BlockStudent;